import React, { useEffect, useMemo, useRef, useState } from 'react'
import { CAlert, CButton, CModal, CModalBody, CModalFooter, CModalHeader, CModalTitle, CSpinner } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { icon } from '../../shared/icons'
import { useUI } from '../../shared/ui/UIProvider'
import { request } from '../../services'
import { REMARK_TYPES } from '../bostonIA/lib/catalogs'

function prettyJson(value) {
  if (!value) return '{}'
  if (typeof value === 'object') return JSON.stringify(value, null, 2)
  try {
    return JSON.stringify(JSON.parse(value), null, 2)
  } catch (_) {
    return String(value)
  }
}

function parseJsonValue(value) {
  if (!value) return null
  if (typeof value === 'object') return value
  try {
    return JSON.parse(value)
  } catch (_) {
    return null
  }
}

function looksLikeDataJson(value) {
  const data = parseJsonValue(value)
  if (!data || typeof data !== 'object') return false
  if (Array.isArray(data.forms)) return true
  if (Array.isArray(data.actions) && (data.title || data.messageinitial || data.postCommands)) return true
  return false
}

function publishedIndexUrl(value, script) {
  const data = parseJsonValue(value)
  return String(
    data?.publish?.url
    || data?.url
    || script?.url
    || '',
  ).trim()
}

async function fetchPublishedDataJson(value, script) {
  const indexUrl = publishedIndexUrl(value, script)
  if (!indexUrl) return null
  const res = await request('ai_admin_custom_scripts_published_file', {
    method: 'POST',
    body: { url: indexUrl, filename: 'data.json' },
  })
  if (!res.ok) {
    const detail = res.data?.error || res.data?.message || 'No se pudo cargar data.json publicado'
    throw new Error(detail)
  }
  if (res.data?.dataJson) return res.data.dataJson
  return parseJsonValue(res.data?.content)
}

function isBlankConfig(value) {
  if (value == null) return true
  if (typeof value === 'object') return Object.keys(value).length === 0
  const text = String(value).trim()
  return !text || text === '{}' || text === 'null' || text === 'undefined'
}

function pickEditableConfig(ownConfig, templateConfig) {
  return isBlankConfig(ownConfig) && !isBlankConfig(templateConfig) ? templateConfig : ownConfig
}

function firstNonBlankConfig(...values) {
  return values.find((value) => !isBlankConfig(value)) || null
}

function fieldKey(field) {
  return String(field?.campoForm || field?.tag || field?.id || field?.name || '').trim()
}

function firstValue(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== '') || ''
}

function isRemarkConfigurableField(field) {
  if (!field || typeof field !== 'object') return false
  const mask = String(field.remark_mask || field.remark || '').trim()
  const type = String(field.typeremark || '').trim()
  const block = String(field.block ?? '').trim()
  if (type) return true
  if (['3', '5', '6', '7', '9'].includes(block)) return true
  if (/^(3|5|6|7|9|5h-|5[¥*.-]|h-|[¥*-])/i.test(mask)) return true
  return false
}

function collectConfigurableFields(dataJson) {
  const byKey = new Map()
  const forms = Array.isArray(dataJson?.forms) ? dataJson.forms : []
  forms.forEach((form) => {
    ;[...(Array.isArray(form.remarks) ? form.remarks : []), ...(Array.isArray(form.fields) ? form.fields : [])].forEach((field) => {
      const key = fieldKey(field)
      if (!key || byKey.has(key)) return
      byKey.set(key, {
        ...field,
        __formTitle: form.title || form.form || '',
      })
    })
  })
  return Array.from(byKey.values())
}

function isPublishMetadataOnly(value) {
  const config = parseJsonValue(value)
  if (!config || typeof config !== 'object' || Array.isArray(config)) return false
  const keys = Object.keys(config)
  return keys.length === 1 && Boolean(config.publish && typeof config.publish === 'object')
}

function normalizeOverlayConfig(value) {
  const config = parseJsonValue(value)
  if (!config || typeof config !== 'object' || Array.isArray(config)) return {}
  if (looksLikeDataJson(config) || isPublishMetadataOnly(config)) return {}
  return config
}

function emptyFieldOverride(override) {
  if (!override || typeof override !== 'object') return true
  return Object.keys(override).every((key) => override[key] === '' || override[key] == null)
}

function cleanOverlayConfig(config) {
  const next = { ...(config && typeof config === 'object' && !Array.isArray(config) ? config : {}) }
  if (next.fields && typeof next.fields === 'object' && !Array.isArray(next.fields)) {
    const fields = {}
    Object.keys(next.fields).forEach((key) => {
      if (!emptyFieldOverride(next.fields[key])) fields[key] = next.fields[key]
    })
    if (Object.keys(fields).length) next.fields = fields
    else delete next.fields
  }
  ;['hiddenFields', 'requiredFields', 'optionalFields', 'readonlyFields'].forEach((key) => {
    if (Array.isArray(next[key]) && next[key].length === 0) delete next[key]
  })
  return next
}

function fieldOverride(config, key) {
  return config?.fields?.[key] || {}
}

function hasListValue(config, listName, key) {
  return Array.isArray(config?.[listName]) && config[listName].includes(key)
}

function effectiveFieldValue(field, config) {
  const key = fieldKey(field)
  const override = fieldOverride(config, key)
  const hiddenByList = hasListValue(config, 'hiddenFields', key)
  const requiredByList = hasListValue(config, 'requiredFields', key)
  const readonlyByList = hasListValue(config, 'readonlyFields', key)
  const visible = override.visible !== undefined
    ? Boolean(override.visible)
    : override.hidden !== undefined
      ? !Boolean(override.hidden)
      : hiddenByList
        ? false
        : field.visible !== false
  const mandatory = override.mandatory !== undefined
    ? Boolean(override.mandatory)
    : override.required !== undefined
      ? Boolean(override.required)
      : requiredByList
        ? true
        : Boolean(field.mandatory)
  return {
    key,
    label: override.label !== undefined ? String(override.label || '') : String(field.label || key),
    visible,
    mandatory,
    readonly: override.readonly !== undefined ? Boolean(override.readonly) : readonlyByList,
    defaultValue: firstValue(override.default, override.defaultValue, override.default_value, override.value, override.dato),
    remarkConfigurable: isRemarkConfigurableField(field),
    typeremark: firstValue(override.typeremark, override.typeRemark, override.type_remark, override.remarkType, override.remark_type, override.tipoRemark, override.tipo_remark, field.typeremark),
  }
}

function ScriptConfigModal({ open, onClose, kind, target, script, onSaved }) {
  const { toast } = useUI()
  const scopeLabel = kind === 'pcc' ? `PCC ${target.pcc}` : `usuario ${target.username}`
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [text, setText] = useState('')
  const [config, setConfig] = useState({})
  const [dataJson, setDataJson] = useState(null)
  const [meta, setMeta] = useState({ name: '', description: '' })
  const [error, setError] = useState('')
  const configRef = useRef({})
  const fields = useMemo(() => collectConfigurableFields(dataJson), [dataJson])

  function syncConfig(nextConfig) {
    const cleaned = cleanOverlayConfig(nextConfig)
    configRef.current = cleaned
    setConfig(cleaned)
    setText(prettyJson(cleaned))
  }

  function updateField(key, patch) {
    const currentConfig = configRef.current || {}
    syncConfig({
      ...currentConfig,
      fields: {
        ...(currentConfig.fields || {}),
        [key]: {
          ...(currentConfig.fields?.[key] || {}),
          ...patch,
        },
      },
    })
  }

  useEffect(() => {
    if (!open || !script) return
    let active = true
    async function load() {
      setLoading(true)
      setError('')
      setText('')
      configRef.current = {}
      setConfig({})
      setDataJson(null)
      const res = await request('script_config', {
        params: { kind, uuid: script.uuid, username: target.username, pcc: target.pcc, parent_pcc: target.parent_pcc },
      })
      if (!active) return
      setLoading(false)
      if (!res.ok) {
        const detail = res.data?.error
          || (typeof res.data === 'string' ? res.data : '')
          || `El servidor respondio ${res.status || 'error'} al pedir la configuracion`
        setError(detail)
        return
      }
      const templateSource = firstNonBlankConfig(
        script.config_template,
        script.configTemplate,
        res.data?.config_template,
        res.data?.configTemplate,
        res.data?.templateConfig,
      )
      const editableConfig = pickEditableConfig(res.data?.config, templateSource)
      setMeta({ name: res.data?.name || script.name || '', description: res.data?.description || '' })
      const overlayConfig = normalizeOverlayConfig(res.data?.config)
      syncConfig(overlayConfig)
      if (looksLikeDataJson(templateSource)) {
        setDataJson(parseJsonValue(templateSource))
        return
      }
      try {
        const publishedDataJson = await fetchPublishedDataJson(templateSource || editableConfig, script)
        if (!active) return
        if (publishedDataJson && looksLikeDataJson(publishedDataJson)) {
          setDataJson(publishedDataJson)
          return
        }
      } catch (fetchError) {
        setError(`${fetchError.message}. Se muestra la configuracion disponible.`)
      }
      if (!isBlankConfig(editableConfig) && !isPublishMetadataOnly(editableConfig)) {
        syncConfig(normalizeOverlayConfig(editableConfig))
      }
    }
    load()
    return () => { active = false }
  }, [open, script, kind, target.pcc, target.username])

  function format() {
    try {
      const parsed = cleanOverlayConfig(JSON.parse(text))
      configRef.current = parsed
      setConfig(parsed)
      setText(JSON.stringify(parsed, null, 2))
      setError('')
    } catch (parseError) {
      setError(`JSON invalido: ${parseError.message}`)
    }
  }

  async function save() {
    let normalized
    try {
      const parsed = cleanOverlayConfig(JSON.parse(text))
      configRef.current = parsed
      setConfig(parsed)
      setText(JSON.stringify(parsed, null, 2))
      normalized = JSON.stringify(parsed)
    } catch (parseError) {
      setError(`JSON invalido: ${parseError.message}`)
      toast('El JSON tiene un error de sintaxis', 'danger')
      return
    }
    setSaving(true)
    const action = kind === 'pcc' ? 'pcc_script_config_save' : 'user_script_config_save'
    const res = await request(action, {
      method: 'POST',
      body: {
        ...target,
        uuid: script.uuid,
        config: normalized,
        status: script.status ?? 1,
        name: meta.name || script.name || script.namescript || '',
        namescript: meta.name || script.name || script.namescript || '',
        updated_user: target.updated_user || target.username || '',
      },
    })
    setSaving(false)
    if (!res.ok) {
      const detail = res.data?.error || 'No se pudo guardar la configuracion'
      setError(detail)
      toast(detail, 'danger')
      return
    }
    toast(`Configuracion guardada para "${meta.name || script.name}"`, 'success')
    if (onSaved) onSaved()
    onClose()
  }

  return (
    <CModal visible={open} onClose={onClose} size="lg" alignment="center" scrollable>
      <CModalHeader>
        <CModalTitle>
          <CIcon icon={icon('cilSettings')} className="me-2" />
          Configuracion de {meta.name || script?.name || 'script'}
        </CModalTitle>
      </CModalHeader>
      <CModalBody>
        <p className="config-scope-note">
          Editas la configuracion aplicada al <strong>{scopeLabel}</strong>. El script publicado se usa solo como plantilla para listar campos.
        </p>
        {error ? <CAlert color="danger" onClose={() => setError('')} dismissible>{error}</CAlert> : null}
        {loading ? (
          <div className="agency-loading"><CSpinner color="success" size="sm" /> Cargando configuracion</div>
        ) : (
          <>
            {fields.length ? (
              <div className="config-field-table">
                <div className="config-field-head">
                  <span>Campo</span>
                  <span>Visible</span>
                  <span>Obligatorio</span>
                  <span>Solo lectura</span>
                  <span>Tipo remark</span>
                  <span>Default</span>
                </div>
                {fields.map((field) => {
                  const view = effectiveFieldValue(field, config)
                  return (
                    <div className="config-field-row" key={view.key}>
                      <div className="config-field-main">
                        <input
                          className="config-field-label-input"
                          value={view.label}
                          onChange={(event) => updateField(view.key, { label: event.target.value })}
                          title={view.key}
                        />
                        <small>{view.key}{field.__formTitle ? ` · ${field.__formTitle}` : ''}</small>
                      </div>
                      <label className="config-toggle">
                        <input type="checkbox" checked={view.visible} onChange={(event) => updateField(view.key, { visible: event.target.checked })} />
                      </label>
                      <label className="config-toggle">
                        <input type="checkbox" checked={view.mandatory} onChange={(event) => updateField(view.key, { mandatory: event.target.checked, required: event.target.checked })} />
                      </label>
                      <label className="config-toggle">
                        <input type="checkbox" checked={view.readonly} onChange={(event) => updateField(view.key, { readonly: event.target.checked })} />
                      </label>
                      {view.remarkConfigurable ? (
                        <select
                          className="config-remark-select"
                          value={view.typeremark}
                          onChange={(event) => updateField(view.key, { typeremark: event.target.value })}
                        >
                          <option value="">Sin cambio</option>
                          {REMARK_TYPES.map((type) => (
                            <option key={type.id} value={type.id}>{type.label}</option>
                          ))}
                        </select>
                      ) : (
                        <span className="config-not-applicable">No aplica</span>
                      )}
                      <input
                        className="config-default-input"
                        value={view.defaultValue}
                        onChange={(event) => updateField(view.key, { default: event.target.value })}
                        placeholder="Sin default"
                      />
                    </div>
                  )
                })}
              </div>
            ) : (
              <CAlert color="info">No se pudo leer la plantilla de campos del data.json. Puedes editar el JSON de configuracion manualmente.</CAlert>
            )}
            <details className="config-json-details">
              <summary>JSON de configuracion que se guardara</summary>
              <textarea
                className="config-editor"
                spellCheck={false}
                value={text}
                onChange={(event) => {
                  setText(event.target.value)
                  const parsed = parseJsonValue(event.target.value)
                  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                    const cleaned = cleanOverlayConfig(parsed)
                    configRef.current = cleaned
                    setConfig(cleaned)
                  }
                }}
                placeholder="{}"
              />
            </details>
          </>
        )}
      </CModalBody>
      <CModalFooter>
        <CButton color="light" onClick={format} disabled={loading || saving}>
          <CIcon icon={icon('cilCode')} /> Formatear
        </CButton>
        <CButton color="light" onClick={onClose} disabled={saving}>Cancelar</CButton>
        <CButton color="success" className="btn-brand" onClick={save} disabled={loading || saving}>
          {saving ? <CSpinner size="sm" /> : 'Guardar configuracion'}
        </CButton>
      </CModalFooter>
    </CModal>
  )
}

export default ScriptConfigModal
